import React, { useState, useEffect, useRef } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  NavLink,
  useLocation,
} from "react-router-dom";
import Home from "./Home";
import Merch from "./Merch";
import Fotos from "./Fotos";
import Contato from "./Contato";
import AlbumRLCP from "./RLCP";
import ProductPage from "./ProductPage";
import { ProductDetail } from "./ProductPage";
import "./GlitchTheme.css";

const SHIPPING_TABLE = {
  light: {
    MG_SP: { pac: 18, sedex: 28 },
    RJ_ES_SUL: { pac: 22, sedex: 35 },
    CO_DF: { pac: 26, sedex: 42 },
    OUTROS: { pac: 32, sedex: 52 },
  },
  heavy: {
    MG_SP: { pac: 22, sedex: 35 },
    RJ_ES_SUL: { pac: 28, sedex: 44 },
    CO_DF: { pac: 33, sedex: 52 },
    OUTROS: { pac: 40, sedex: 62 },
  },
};

const PRODUCT_WEIGHTS: Record<string, number> = {
  "W2-CD-STD": 250,
  "W2-CD-SGN": 270,
  "W2-CD-CCE": 450,
  "W2-CD-GKE": 450,
  "W2-MOL-GAL": 800,
  "W2-MOL-TIG": 800,
};

const PROGRESSIVE_DISCOUNT: Record<string, Record<number, number>> = {
  "W2-CD-STD": { 1: 35, 2: 34, 3: 33, 4: 32 },
};

function getDiscountedPrice(
  sku: string,
  basePrice: number,
  quantity: number
): number {
  const table = PROGRESSIVE_DISCOUNT[sku];
  if (!table) return basePrice;
  const keys = Object.keys(table)
    .map(Number)
    .sort((a, b) => b - a);
  for (const qty of keys) {
    if (quantity >= qty) return table[qty];
  }
  return basePrice;
}

function getRegion(uf: string): keyof typeof SHIPPING_TABLE.light {
  if (["MG", "SP"].includes(uf)) return "MG_SP";
  if (["RJ", "ES", "PR", "SC", "RS"].includes(uf)) return "RJ_ES_SUL";
  if (["DF", "GO", "MT", "MS"].includes(uf)) return "CO_DF";
  return "OUTROS";
}

function AppInner() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [cart, setCart] = useState<(ProductDetail & { quantity: number })[]>(
    []
  );
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [cep, setCep] = useState("");
  const [shippingOption, setShippingOption] = useState<"pac" | "sedex" | null>(
    null
  );
  const [isCalculating, setIsCalculating] = useState(false);
  const [address, setAddress] = useState({
    logradouro: "",
    bairro: "",
    localidade: "",
    uf: "",
    erro: false,
  });
  const [houseNumber, setHouseNumber] = useState("");
  const [complement, setComplement] = useState("");
  const cartBtnRef = useRef<HTMLButtonElement>(null);

  const location = useLocation();
  const isRLCP = location.pathname === "/musicas";
  const isGalaxy = !isRLCP;

  const closeMenu = () => setIsMobileMenuOpen(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    closeMenu();
  }, [location.pathname]);

  const totalWeight = cart.reduce((acc, item) => {
    const w = PRODUCT_WEIGHTS[item.sku] ?? 300;
    return acc + w * item.quantity;
  }, 0);
  const weightClass = totalWeight > 300 ? "heavy" : "light";
  const shippingRates = address.uf
    ? SHIPPING_TABLE[weightClass][getRegion(address.uf)]
    : null;
  const shippingVal =
    shippingOption && shippingRates ? shippingRates[shippingOption] : 0;

  useEffect(() => {
    if (isGalaxy) document.body.classList.add("galaxy-page");
    else document.body.classList.remove("galaxy-page");
    return () => document.body.classList.remove("galaxy-page");
  }, [isGalaxy]);

  useEffect(() => {
    if (cep.length !== 8) {
      setAddress({
        logradouro: "",
        bairro: "",
        localidade: "",
        uf: "",
        erro: false,
      });
      setShippingOption(null);
      return;
    }
    setIsCalculating(true);
    fetch(`https://viacep.com.br/ws/${cep}/json/`)
      .then((r) => r.json())
      .then((data) => {
        if (data.erro) {
          setAddress({
            logradouro: "",
            bairro: "",
            localidade: "",
            uf: "",
            erro: true,
          });
          setShippingOption(null);
        } else {
          setAddress({
            logradouro: data.logradouro,
            bairro: data.bairro,
            localidade: data.localidade,
            uf: data.uf,
            erro: false,
          });
          setShippingOption(null);
        }
      })
      .catch(() => {
        setAddress({
          logradouro: "",
          bairro: "",
          localidade: "",
          uf: "",
          erro: true,
        });
        setShippingOption(null);
      })
      .finally(() => setIsCalculating(false));
  }, [cep]);

  useEffect(() => {
    const btn = cartBtnRef.current;
    if (!btn) return;
    const onMove = (e: MouseEvent) => {
      const r = btn.getBoundingClientRect();
      btn.style.setProperty(
        "--mag-x",
        `${(e.clientX - r.left - r.width / 2) * 0.2}px`
      );
      btn.style.setProperty(
        "--mag-y",
        `${(e.clientY - r.top - r.height / 2) * 0.2}px`
      );
    };
    const onLeave = () => {
      btn.style.setProperty("--mag-x", "0px");
      btn.style.setProperty("--mag-y", "0px");
    };
    btn.addEventListener("mousemove", onMove);
    btn.addEventListener("mouseleave", onLeave);
    return () => {
      btn.removeEventListener("mousemove", onMove);
      btn.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  const addToCart = (product: ProductDetail) => {
    if (product.unavailable) return;
    const cartQty = cart.find((i) => i.id === product.id)?.quantity ?? 0;
    if (product.stockLimit !== undefined && cartQty >= product.stockLimit)
      return;
    setCart((prev) => {
      const exists = prev.find((i) => i.id === product.id);
      return exists
        ? prev.map((i) =>
            i.id === product.id ? { ...i, quantity: i.quantity + 1 } : i
          )
        : [...prev, { ...product, quantity: 1 }];
    });
    setIsCartOpen(true);
  };

  const removeFromCart = (id: number) =>
    setCart((prev) => prev.filter((i) => i.id !== id));

  const updateQty = (id: number, delta: number) => {
    setCart((prev) =>
      prev
        .map((i) => {
          if (i.id !== id) return i;
          const newQty = i.quantity + delta;
          if (newQty <= 0) return { ...i, quantity: 0 };
          if (i.stockLimit !== undefined && newQty > i.stockLimit) return i;
          return { ...i, quantity: newQty };
        })
        .filter((i) => i.quantity > 0)
    );
  };

  const totalQty = cart.reduce((a, b) => a + b.quantity, 0);
  const itemsTotal = cart.reduce((acc, item) => {
    const discountedPrice = getDiscountedPrice(
      item.sku,
      item.price,
      item.quantity
    );
    return acc + discountedPrice * item.quantity;
  }, 0);
  const finalTotal = itemsTotal + shippingVal;
  const canCheckout =
    cart.length > 0 &&
    customerName.trim() !== "" &&
    cep.length === 8 &&
    !address.erro &&
    houseNumber.trim() !== "" &&
    shippingOption !== null;

  const buildWhatsAppMsg = () => {
    let msg = `*SYS.ORDER // NOVO PEDIDO* [ STATUS: OK ]\n\n*CLIENTE:* ${customerName}\n\n*ITENS:*\n`;
    cart.forEach((i) => {
      const dp = getDiscountedPrice(i.sku, i.price, i.quantity);
      msg += `- ${i.quantity}x ${i.name} (SKU: ${i.sku}) @ R$ ${dp.toFixed(
        2
      )}/un${i.preOrder ? " [PRÉ-VENDA]" : ""}\n`;
    });
    msg += `\n*ENDEREÇO:*\n- Rua: ${address.logradouro}\n- Nº: ${houseNumber}\n`;
    if (complement) msg += `- Complemento: ${complement}\n`;
    msg += `- Bairro: ${address.bairro}\n- ${address.localidade}/${address.uf}\n- CEP: ${cep}`;
    msg += `\n\n*ENVIO:* ${shippingOption?.toUpperCase()} — R$ ${shippingVal.toFixed(
      2
    )}`;
    msg += `\n\n*RESUMO:*\n- Subtotal: R$ ${itemsTotal.toFixed(
      2
    )}\n- Frete (${shippingOption?.toUpperCase()}): R$ ${shippingVal.toFixed(
      2
    )}`;
    msg += `\n\n*TOTAL: R$ ${finalTotal.toFixed(2)}*\n\nAguardando chave PIX.`;
    return `https://wa.me/5535998371336?text=${encodeURIComponent(msg)}`;
  };

  const showCart =
    isGalaxy &&
    (location.pathname === "/merch" || location.pathname.startsWith("/merch/"));

  const qtyBtnStyle: React.CSSProperties = {
    background: "transparent",
    border: "1px solid rgba(200,127,255,0.3)",
    color: "#c87fff",
    width: "24px",
    height: "24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    fontFamily: "Space Mono, monospace",
    fontSize: "0.85rem",
    lineHeight: 1,
    flexShrink: 0,
    transition: "background 0.2s",
  };

  const shippingOptStyle = (opt: "pac" | "sedex"): React.CSSProperties => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0.6rem 0.8rem",
    border: `1px solid ${
      shippingOption === opt ? "#c87fff" : "rgba(180,100,255,0.2)"
    }`,
    background:
      shippingOption === opt ? "rgba(200,127,255,0.08)" : "transparent",
    cursor: "pointer",
    transition: "all 0.2s",
    marginBottom: "0.5rem",
  });

  const mobileMenuStyle: React.CSSProperties = isMobile
    ? {
        display: isMobileMenuOpen ? "flex" : "none",
        position: "fixed",
        inset: 0,
        background: "rgba(5,5,5,0.98)",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: "3rem",
        zIndex: 1000,
        listStyle: "none",
        margin: 0,
        padding: 0,
      }
    : {};

  const inputStyle: React.CSSProperties = {
    background: "rgba(10,2,25,0.8)",
    borderColor: "rgba(180,100,255,0.2)",
    color: "#c87fff",
  };

  return (
    <>
      {isRLCP && (
        <>
          <div className="animated-bg">
            <div className="animated-grid"></div>
          </div>
          <div className="vignette-overlay"></div>
        </>
      )}

      <div className="app-container" style={{ paddingTop: 0 }}>
        <nav className={`navbar ${isGalaxy ? "navbar-galaxy" : ""}`}>
          <div
            className="nav-logo"
            style={{ zIndex: 1002, display: "flex", alignItems: "center" }}
          >
            <Link
              to="/"
              style={{ textDecoration: "none", color: "inherit" }}
              onClick={closeMenu}
            >
              <span className={isGalaxy ? "galaxy-accent" : "neon-green"}>
                Eduardo
              </span>{" "}
              Bertozzi
            </Link>
            <span
              className={`status-dot ${isGalaxy ? "status-dot-galaxy" : ""}`}
              title="SYS_ONLINE"
            />
          </div>

          {isMobile && (
            <button
              className="mobile-toggle"
              style={{
                display: "block",
                color: isGalaxy ? "#c87fff" : "#39ff14",
              }}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? "[ X ]" : "[ MENU ]"}
            </button>
          )}

          <ul
            className={`nav-links ${isGalaxy ? "nav-links-galaxy" : ""}`}
            style={mobileMenuStyle}
          >
            <li>
              <NavLink
                to="/"
                end
                onClick={closeMenu}
                style={isMobile ? { fontSize: "1.5rem" } : {}}
              >
                home
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/musicas"
                onClick={closeMenu}
                style={isMobile ? { fontSize: "1.5rem" } : {}}
              >
                RLCP
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/merch"
                onClick={closeMenu}
                style={isMobile ? { fontSize: "1.5rem" } : {}}
              >
                Merch
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/fotos"
                onClick={closeMenu}
                style={isMobile ? { fontSize: "1.5rem" } : {}}
              >
                fotos
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/contato"
                onClick={closeMenu}
                style={isMobile ? { fontSize: "1.5rem" } : {}}
              >
                contato
              </NavLink>
            </li>
          </ul>
        </nav>

        {showCart && (
          <>
            <button
              ref={cartBtnRef}
              onClick={() => setIsCartOpen(!isCartOpen)}
              className="cart-toggle-btn magnetic"
              style={{
                background: "rgba(13,1,32,0.95)",
                border: "1px solid rgba(200,127,255,0.5)",
                color: "#c87fff",
                boxShadow:
                  totalQty > 0
                    ? "0 0 20px rgba(200,127,255,0.4)"
                    : "0 0 15px rgba(200,127,255,0.15)",
                animation:
                  totalQty > 0 ? "pulseGalaxy 2s infinite alternate" : "none",
                ...(isMobile
                  ? {
                      top: "auto",
                      bottom: "1.5rem",
                      right: "1.5rem",
                      fontSize: "0.75rem",
                      padding: "0.6rem 1rem",
                    }
                  : {}),
              }}
            >
              SYS.CART ({totalQty})
            </button>

            <aside
              className={`cart-drawer ${isCartOpen ? "open" : ""}`}
              style={{
                background: "rgba(10,2,25,0.98)",
                borderLeft: "1px solid rgba(200,127,255,0.3)",
                ...(isMobile ? { maxWidth: "100%" } : {}),
              }}
            >
              <div
                className="cart-header"
                style={{ borderBottom: "1px solid rgba(180,100,255,0.15)" }}
              >
                <h2
                  className="cart-title"
                  style={{
                    color: "#c87fff",
                    textShadow: "0 0 12px rgba(180,80,255,0.6)",
                  }}
                >
                  SYS_CHECKOUT
                </h2>
                <button
                  className="cart-close"
                  onClick={() => setIsCartOpen(false)}
                >
                  [ X ]
                </button>
              </div>

              <div className="cart-items">
                {cart.length === 0 ? (
                  <p className="cart-empty">&gt; CARRINHO VAZIO.</p>
                ) : (
                  cart.map((item) => {
                    const discountedPrice = getDiscountedPrice(
                      item.sku,
                      item.price,
                      item.quantity
                    );
                    const hasDiscount = discountedPrice < item.price;
                    return (
                      <div
                        key={item.id}
                        className="cart-item"
                        style={{
                          background: "rgba(20,5,40,0.6)",
                          border: "1px solid rgba(180,100,255,0.12)",
                        }}
                      >
                        <span className="cart-item-name">
                          {item.name}
                          {item.preOrder && (
                            <span
                              style={{
                                color: "#c87fff",
                                fontSize: "0.65rem",
                                marginLeft: "0.5rem",
                              }}
                            >
                              [PRÉ-VENDA]
                            </span>
                          )}
                        </span>
                        {hasDiscount && (
                          <span
                            style={{
                              display: "inline-block",
                              marginTop: "0.25rem",
                              fontSize: "0.6rem",
                              color: "#0d0120",
                              background: "#c87fff",
                              padding: "1px 6px",
                              letterSpacing: "1px",
                              fontFamily: "Space Mono, monospace",
                            }}
                          >
                            R$ {discountedPrice}/UN
                          </span>
                        )}
                        <div
                          className="cart-item-row"
                          style={{
                            marginTop: "0.6rem",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                            }}
                          >
                            <button
                              style={qtyBtnStyle}
                              onClick={() => updateQty(item.id, -1)}
                              onMouseOver={(e) => {
                                (
                                  e.currentTarget as HTMLButtonElement
                                ).style.background = "rgba(200,127,255,0.15)";
                              }}
                              onMouseOut={(e) => {
                                (
                                  e.currentTarget as HTMLButtonElement
                                ).style.background = "transparent";
                              }}
                            >
                              −
                            </button>
                            <span
                              style={{
                                color: "#e0b8ff",
                                fontFamily: "Space Mono, monospace",
                                fontSize: "0.85rem",
                                minWidth: "20px",
                                textAlign: "center",
                              }}
                            >
                              {item.quantity}
                            </span>
                            <button
                              style={{
                                ...qtyBtnStyle,
                                opacity:
                                  item.stockLimit !== undefined &&
                                  item.quantity >= item.stockLimit
                                    ? 0.3
                                    : 1,
                                cursor:
                                  item.stockLimit !== undefined &&
                                  item.quantity >= item.stockLimit
                                    ? "not-allowed"
                                    : "pointer",
                              }}
                              onClick={() => updateQty(item.id, 1)}
                              disabled={
                                item.stockLimit !== undefined &&
                                item.quantity >= item.stockLimit
                              }
                              onMouseOver={(e) => {
                                if (
                                  !(
                                    item.stockLimit !== undefined &&
                                    item.quantity >= item.stockLimit
                                  )
                                )
                                  (
                                    e.currentTarget as HTMLButtonElement
                                  ).style.background = "rgba(200,127,255,0.15)";
                              }}
                              onMouseOut={(e) => {
                                (
                                  e.currentTarget as HTMLButtonElement
                                ).style.background = "transparent";
                              }}
                            >
                              +
                            </button>
                          </div>
                          <div
                            style={{
                              flex: 1,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-end",
                            }}
                          >
                            {hasDiscount && (
                              <span
                                style={{
                                  color: "rgba(200,160,255,0.35)",
                                  fontFamily: "Space Mono, monospace",
                                  fontSize: "0.7rem",
                                  textDecoration: "line-through",
                                }}
                              >
                                R$ {(item.price * item.quantity).toFixed(2)}
                              </span>
                            )}
                            <span
                              style={{
                                color: "#c87fff",
                                textShadow: "0 0 12px rgba(180,80,255,0.6)",
                                fontSize: "0.85rem",
                              }}
                            >
                              R$ {(discountedPrice * item.quantity).toFixed(2)}
                            </span>
                          </div>
                          <button
                            className="cart-remove-btn"
                            onClick={() => removeFromCart(item.id)}
                          >
                            X
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {cart.some((i) => PROGRESSIVE_DISCOUNT[i.sku]) && (
                <div
                  style={{
                    padding: "0.75rem",
                    margin: "0 0 0.75rem",
                    background: "rgba(100,20,180,0.06)",
                    border: "1px solid rgba(200,127,255,0.12)",
                    fontSize: "0.62rem",
                    fontFamily: "Space Mono, monospace",
                    color: "rgba(200,160,255,0.5)",
                  }}
                >
                  <span
                    style={{
                      display: "block",
                      marginBottom: "0.4rem",
                      color: "rgba(200,160,255,0.7)",
                    }}
                  >
                    ⟡ DESCONTO PROGRESSIVO
                  </span>
                  <span>
                    1 UN = R$ 35 · 2 UN = R$ 34 · 3 UN = R$ 33 · 4+ UN = R$ 32
                  </span>
                </div>
              )}

              <div
                className="cart-footer"
                style={{ borderTop: "1px solid rgba(180,100,255,0.15)" }}
              >
                {/* Nome completo */}
                <div className="cep-block">
                  <label className="cart-label">&gt; NOME_COMPLETO:</label>
                  <input
                    type="text"
                    className="cart-input"
                    style={inputStyle}
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="Seu nome completo"
                  />
                </div>

                {/* CEP */}
                <div className="cep-block">
                  <label className="cart-label">
                    &gt; INSERIR_CEP_PARA_ENTREGA:
                  </label>
                  <div className="cep-input-wrap">
                    <input
                      type="text"
                      className="cart-input"
                      style={inputStyle}
                      value={cep}
                      onChange={(e) =>
                        setCep(
                          e.target.value.replace(/\D/g, "").substring(0, 8)
                        )
                      }
                      placeholder="Apenas números"
                    />
                    {isCalculating && (
                      <span
                        className="cep-calculating"
                        style={{ color: "#c87fff" }}
                      >
                        CALCULANDO...
                      </span>
                    )}
                  </div>
                  {address.erro && (
                    <p className="cep-error">&gt; CEP INVÁLIDO</p>
                  )}
                </div>

                {address.logradouro && (
                  <div
                    className="address-block"
                    style={{
                      background: "rgba(20,5,40,0.4)",
                      border: "1px dashed rgba(180,100,255,0.2)",
                    }}
                  >
                    <p className="address-text">
                      {address.logradouro}, {address.bairro}
                      <br />
                      {address.localidade} — {address.uf}
                    </p>
                    <div className="address-inputs">
                      <input
                        type="text"
                        className="cart-input address-num"
                        style={inputStyle}
                        value={houseNumber}
                        onChange={(e) => setHouseNumber(e.target.value)}
                        placeholder="Nº"
                      />
                      <input
                        type="text"
                        className="cart-input address-comp"
                        style={inputStyle}
                        value={complement}
                        onChange={(e) => setComplement(e.target.value)}
                        placeholder="Complemento (opcional)"
                      />
                    </div>
                  </div>
                )}

                {shippingRates && (
                  <div style={{ marginBottom: "1rem" }}>
                    <label className="cart-label">
                      &gt; SELECIONAR_MODALIDADE:
                    </label>
                    <div
                      style={shippingOptStyle("pac")}
                      onClick={() => setShippingOption("pac")}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "2px",
                        }}
                      >
                        <span
                          style={{
                            color:
                              shippingOption === "pac"
                                ? "#e0b8ff"
                                : "rgba(200,160,255,0.7)",
                            fontFamily: "Space Mono, monospace",
                            fontSize: "0.8rem",
                            fontWeight: "bold",
                          }}
                        >
                          PAC
                        </span>
                        <span
                          style={{
                            color: "rgba(180,120,255,0.4)",
                            fontFamily: "Space Mono, monospace",
                            fontSize: "0.65rem",
                          }}
                        >
                          5–8 dias úteis
                        </span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <span
                          style={{
                            color:
                              shippingOption === "pac"
                                ? "#c87fff"
                                : "rgba(200,160,255,0.6)",
                            fontFamily: "Space Mono, monospace",
                            fontSize: "0.85rem",
                            fontWeight: "bold",
                          }}
                        >
                          R$ {shippingRates.pac.toFixed(2)}
                        </span>
                        <div
                          style={{
                            width: "14px",
                            height: "14px",
                            borderRadius: "50%",
                            border: `2px solid ${
                              shippingOption === "pac"
                                ? "#c87fff"
                                : "rgba(200,127,255,0.3)"
                            }`,
                            background:
                              shippingOption === "pac"
                                ? "#c87fff"
                                : "transparent",
                            transition: "all 0.2s",
                          }}
                        />
                      </div>
                    </div>
                    <div
                      style={shippingOptStyle("sedex")}
                      onClick={() => setShippingOption("sedex")}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "2px",
                        }}
                      >
                        <span
                          style={{
                            color:
                              shippingOption === "sedex"
                                ? "#e0b8ff"
                                : "rgba(200,160,255,0.7)",
                            fontFamily: "Space Mono, monospace",
                            fontSize: "0.8rem",
                            fontWeight: "bold",
                          }}
                        >
                          SEDEX
                        </span>
                        <span
                          style={{
                            color: "rgba(180,120,255,0.4)",
                            fontFamily: "Space Mono, monospace",
                            fontSize: "0.65rem",
                          }}
                        >
                          1–3 dias úteis
                        </span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <span
                          style={{
                            color:
                              shippingOption === "sedex"
                                ? "#c87fff"
                                : "rgba(200,160,255,0.6)",
                            fontFamily: "Space Mono, monospace",
                            fontSize: "0.85rem",
                            fontWeight: "bold",
                          }}
                        >
                          R$ {shippingRates.sedex.toFixed(2)}
                        </span>
                        <div
                          style={{
                            width: "14px",
                            height: "14px",
                            borderRadius: "50%",
                            border: `2px solid ${
                              shippingOption === "sedex"
                                ? "#c87fff"
                                : "rgba(200,127,255,0.3)"
                            }`,
                            background:
                              shippingOption === "sedex"
                                ? "#c87fff"
                                : "transparent",
                            transition: "all 0.2s",
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div className="cart-totals">
                  <div className="cart-total-row">
                    <span>ITENS:</span>
                    <span>R$ {itemsTotal.toFixed(2)}</span>
                  </div>
                  <div className="cart-total-row">
                    <span>FRETE:</span>
                    <span
                      style={
                        shippingVal > 0
                          ? {
                              color: "#c87fff",
                              textShadow: "0 0 12px rgba(180,80,255,0.6)",
                            }
                          : { color: "rgba(200,160,255,0.3)" }
                      }
                    >
                      {shippingVal > 0 ? `R$ ${shippingVal.toFixed(2)}` : "---"}
                    </span>
                  </div>
                  <div
                    className="cart-total-row cart-total-final"
                    style={{ borderTop: "1px solid rgba(180,100,255,0.15)" }}
                  >
                    <span>TOTAL:</span>
                    <span
                      style={{
                        color: "#c87fff",
                        textShadow: "0 0 12px rgba(180,80,255,0.6)",
                      }}
                    >
                      R$ {finalTotal.toFixed(2)}
                    </span>
                  </div>
                </div>

                {canCheckout ? (
                  <a
                    href={buildWhatsAppMsg()}
                    target="_blank"
                    rel="noreferrer"
                    className="wonder-galaxy-btn checkout-btn"
                  >
                    <span>FINALIZAR COMPRA</span>
                  </a>
                ) : (
                  <button className="wonder-galaxy-btn checkout-btn" disabled>
                    <span>
                      {cart.length === 0
                        ? "CARRINHO VAZIO"
                        : customerName.trim() === ""
                        ? "INFORME O NOME"
                        : !address.logradouro
                        ? "INFORME O CEP"
                        : !houseNumber
                        ? "INFORME O Nº"
                        : "SELECIONE O FRETE"}
                    </span>
                  </button>
                )}
              </div>
            </aside>

            {isCartOpen && (
              <div
                className="cart-overlay"
                onClick={() => setIsCartOpen(false)}
              />
            )}
          </>
        )}

        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/musicas" element={<AlbumRLCP />} />
          <Route
            path="/merch"
            element={<Merch cart={cart} onAddToCart={addToCart} />}
          />
          <Route
            path="/merch/:sku"
            element={<ProductPage cart={cart} onAddToCart={addToCart} />}
          />
          <Route path="/fotos" element={<Fotos />} />
          <Route path="/contato" element={<Contato />} />
        </Routes>
      </div>
    </>
  );
}

export default function App() {
  return (
    <Router>
      <AppInner />
    </Router>
  );
}
